import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Printer } from "lucide-react";
import { formatPrice, formatHour } from "@/lib/utils";

export default function ReceiptModal({ open, onOpenChange, booking, t }) {
  if (!booking) return null;

  const payLabel = t("bookings.payment." + booking.payment_status, booking.payment_status);
  const slotHours = (booking.end_hour || 0) - (booking.start_hour || 0);

  const handlePrint = () => {
    const printWin = window.open("", "_blank");
    if (!printWin) { window.print(); return; }
    printWin.document.write(`
      <html>
      <head>
        <title>Receipt — ${booking.id}</title>
        <style>
          @page { margin: 20mm; }
          body { font-family: 'Courier New', monospace; color: #111; font-size: 13px; line-height: 1.6; max-width: 400px; margin: 0 auto; }
          h1 { font-size: 22px; font-weight: 900; letter-spacing: 2px; text-transform: uppercase; margin: 0 0 4px; }
          .sub { color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
          .divider { border-top: 1px dashed #999; margin: 16px 0; }
          .row { display: flex; justify-content: space-between; padding: 2px 0; }
          .total { font-size: 18px; font-weight: 700; }
          .label { color: #666; }
          .footer { margin-top: 24px; text-align: center; font-size: 10px; color: #999; }
          .badge { display: inline-block; border: 1px solid #111; padding: 2px 8px; font-size: 10px; letter-spacing: 1px; }
    </style>
      </head>
      <body>
        <div style="text-align:center;margin-bottom:16px;">
          <h1>Go<span style="color:#CCFF00">Captain</span></h1>
          <div class="sub">Payment Receipt</div>
        </div>
        <div class="divider"></div>
        <div class="row"><span class="label">Receipt #</span><span>${booking.id}</span></div>
        <div class="row"><span class="label">Date</span><span>${booking.date}</span></div>
        <div class="row"><span class="label">Time</span><span>${formatHour(booking.start_hour)} – ${formatHour(booking.end_hour)} (${slotHours}h)</span></div>
        <div class="row"><span class="label">Court</span><span>${booking.court_name}</span></div>
        <div class="row"><span class="label">Player</span><span>${booking.user_name || booking.user_email}</span></div>
        <div class="row"><span class="label">Payment</span><span>${t("bookings.payment_method_" + booking.payment_method, booking.payment_method).toUpperCase()} · ${payLabel}</span></div>
        ${booking.status ? `<div style="margin-top:8px;"><span class="badge">${booking.status.toUpperCase()}</span></div>` : ""}
        <div class="divider"></div>
        <div class="row"><span class="label">Slot (${slotHours}h × ${formatPrice(booking.total_price - (booking.extras_total || 0), booking.currency)}/h)</span><span>${formatPrice(booking.total_price - (booking.extras_total || 0), booking.currency)}</span></div>
        ${booking.discount_label && booking.discount_amount > 0 ? `<div class="row" style="color:#059669;"><span class="label">${booking.discount_label}</span><span>-${formatPrice(booking.discount_amount, booking.currency)}</span></div>` : ""}
        ${(booking.selected_extras || []).map((e) => `
          <div class="row" style="font-size:12px;"><span class="label">  ${e.name}${e.qty > 1 ? ` ×${e.qty}` : ""}</span><span>+${formatPrice(e.price * (e.qty || 1), booking.currency)}</span></div>
        `).join("")}
        <div class="divider"></div>
        <div class="row total"><span>TOTAL</span><span>${formatPrice(booking.total_price, booking.currency)}</span></div>
        <div class="footer">Thank you for booking with Go<span style="color:#CCFF00">Captain</span></div>
      </body>
      </html>
    `);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => printWin.print(), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-background border border-border text-foreground max-w-md p-0 gap-0 [&>button:last-child]:start-4 [&>button:last-child]:end-auto">
        <div className="flex items-center justify-between p-4 ps-12 border-b border-border">
          <span className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground/80">{t("receipt.title")}</span>
          <button
            onClick={handlePrint}
            className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1.5"
          >
            <Printer className="h-3 w-3" /> {t("receipt.print")}
          </button>
        </div>

        <div className="p-6 space-y-4 font-mono text-sm">
          {/* Header */}
          <div className="text-center pb-4 border-b border-dashed border-border">
            <div className="font-heading text-2xl tracking-[0.15em]">GOCAPTAIN</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-muted-foreground/60 mt-1">{t("receipt.receipt")}</div>
          </div>

          {/* Receipt ID */}
          <div className="flex justify-between text-[11px]">
            <span className="text-muted-foreground/80">{t("receipt.receipt_no")}</span>
            <span className="text-muted-foreground font-mono tracking-tight">{booking.id}</span>
          </div>

          <div className="border-t border-dashed border-border" />

          {/* Details */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground/80">{t("receipt.date")}</span>
              <span>{booking.date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground/80">{t("receipt.time")}</span>
              <span className="font-mono">{formatHour(booking.start_hour)} – {formatHour(booking.end_hour)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground/80">{t("receipt.court")}</span>
              <span className="text-end max-w-[200px] truncate">{booking.court_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground/80">{t("receipt.player")}</span>
              <span className="text-end max-w-[200px] truncate">{booking.user_name || booking.user_email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground/80">{t("receipt.payment_method")}</span>
              <span className="uppercase text-[10px] tracking-widest">{t("bookings.payment_method_" + booking.payment_method, booking.payment_method)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground/80">{t("receipt.payment_status")}</span>
              <span className={`text-[10px] tracking-widest uppercase ${booking.payment_status === "mocked_paid" || booking.payment_status === "paid_at_court" ? "text-emerald-400" : "text-yellow-400"}`}>
                {payLabel}
              </span>
            </div>
          </div>

          <div className="border-t border-dashed border-border" />

          {/* Price Breakdown */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-[12px]">
              <span className="text-muted-foreground/80">{t("receipt.slot_charge", { hours: slotHours })}</span>
              <span>{formatPrice(booking.total_price - (booking.extras_total || 0), booking.currency)}</span>
            </div>
            {booking.discount_label && booking.discount_amount > 0 && (
              <div className="flex justify-between text-[11px]">
                <span className="text-emerald-400">{t("receipt.discount")} · {booking.discount_label}</span>
                <span className="text-emerald-400">&minus;{formatPrice(booking.discount_amount, booking.currency)}</span>
              </div>
            )}
            {(booking.selected_extras || []).map((extra, i) => (
              <div key={i} className="flex justify-between text-[11px] ps-3">
                <span className="text-muted-foreground/80">{extra.name}{extra.qty > 1 ? ` ×${extra.qty}` : ""}</span>
                <span className="text-volt">+{formatPrice(extra.price * (extra.qty || 1), booking.currency)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-dashed border-border" />

          {/* Total */}
          <div className="flex justify-between font-heading text-2xl">
            <span className="text-muted-foreground/80">{t("receipt.total")}</span>
            <span className="text-volt">{formatPrice(booking.total_price, booking.currency)}</span>
          </div>

          <div className="text-center text-[9px] text-muted-foreground/40 tracking-wider pt-4 border-t border-dashed border-border">
            {t("receipt.thank_you")}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
